import reactCSS from 'reactcss';
import { gu2px, Curvature } from '@ux/theme-utils';
import cssSides, { Side } from '@team-griffin/css-sides';
import longhand from '@team-griffin/css-longhand';
import { modifier } from '@ux/style-utils';
import * as r from 'ramda';
import {
  headingXl,
  headingL,
  headingM,
  headingS,
  headingXs,
  content,
  footnote,
} from '@ux/type-size';
import { AvailabilityStatus } from '../../../core/constants';

const getRadius = r.cond([
  [ r.propEq('curvature', Curvature.OFF), r.always(0) ],
  [ r.propEq('curvature', Curvature.WEAK), r.always(3) ],
  [ r.propEq('curvature', Curvature.STRONG), r.always(8) ],
]);

export default ({
  theme,
  isMinSm,
  isMinMd,
  isPromo,
  availability,
  showBundles,
}) => {
  const {
    palette,
    spacing,
    typography,
  } = theme;

  const radius = getRadius(theme);

  return reactCSS({
    default: {
      root: {
        width: '100%',
        marginBottom: gu2px(2, spacing),
        textAlign: 'center',
      },

      body: {
        backgroundColor: palette.primary,
        color: palette.primaryContrast,
        overflow: 'hidden',
        ...longhand('border', {
          topLeftRadius: radius,
          topRightRadius: radius,
          bottomLeftRadius: radius,
          bottomRightRadius: radius,
        }),
      },

      content: {
        ...cssSides('padding', Side.A, gu2px(3, spacing)),
      },

      icon: {
        display: 'inline-block',
        paddingRight: gu2px(1, spacing),
      },

      fqdn: {
        ...headingM(typography),
        display: 'block',
        wordBreak: 'break-all',
      },

      text: {
        ...content(typography),
        marginTop: gu2px(1, spacing),
        marginBottom: gu2px(2, spacing),
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
      },

      badge: {
        marginLeft: r.negate(gu2px(1, spacing)),
      },

      price: {
        ...headingXs(typography),
        marginBottom: gu2px(2, spacing),
        color: palette.primaryContrast,
      },

      oldPrice: {
        textDecoration: 'line-through',
        ...footnote(typography),
        marginLeft: gu2px(1, spacing),
        color: palette.primaryContrast,
      },

      button: {
        marginTop: gu2px(1, spacing),
        minHeight: gu2px(6, spacing),
        lineHeight: `${gu2px(6, spacing)}px`,
      },

      bundle: {
        overflow: 'hidden',
        ...longhand('border', {
          leftWidth: 1,
          bottomWidth: 1,
          rightWidth: 1,
          topWidth: 0,
          color: palette.grey11,
          style: 'solid',
          bottomLeftRadius: radius,
          bottomRightRadius: radius,
        }),
      },

      availability: {
        display: 'block',
        ...content(typography),
        marginBottom: gu2px(1, spacing),
      },

      availabilityIcon: {
        width: gu2px(3, spacing),
        height: gu2px(3, spacing),
        display: 'inline-block',
        verticalAlign: 'middle',
        lineHeight: 1,
        marginRight: gu2px(1, spacing),
      },

      availabilityText: {
        display: 'inline-block',
        verticalAlign: 'middle',
      },

      legal: {
        ...cssSides('margin', Side.X, gu2px(3, spacing)),
        marginBottom: gu2px(2, spacing),
        color: palette.grey08,
      },
    },

    isMinSm: {
      root: {
        textAlign: 'left',
      },

      content: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      },

      info: {
        maxWidth: '60%',
        marginRight: 'auto',
        paddingRight: gu2px(3, spacing),
      },

      fqdn: {
        ...headingL(typography),
        display: 'inline-block',
        verticalAlign: 'middle',
      },

      text: {
        marginBottom: 0,
      },

      availability: {
        display: 'inline-block',
        marginRight: gu2px(2, spacing),
      },

      price: {
        ...headingS(typography),
        marginBottom: 0,
        whiteSpace: 'nowrap',
        textAlign: 'right',
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'flex-end',
        justifyContent: 'center',
      },

      oldPrice: {
        ...content(typography),
      },

      button: {
        whiteSpace: 'nowrap',
        textAlign: 'right',
        paddingLeft: gu2px(2, spacing),
        marginTop: gu2px(2, spacing),
      },
    },

    isMinMd: {
      content: {
        ...cssSides('padding', Side.A, gu2px(4, spacing)),
      },
      fqdn: {
        ...headingXl(typography),
      },
      text: {
        ...headingXs(typography),
      },
      price: {
        ...headingM(typography),
      },
      info: {
        paddingRight: gu2px(6, spacing),
      },
      button: {
        paddingLeft: gu2px(3, spacing),
      },
      legal: {
        ...cssSides('margin', Side.X, gu2px(4, spacing)),
      },
    },

    [modifier('availability', AvailabilityStatus.VERIFYING)]: {
      availability: {
        color: palette.grey12,
      },
    },

    [modifier('availability', AvailabilityStatus.AVAILABLE)]: {
      availability: {
        color: palette.primaryContrast,
        fill: palette.green01,
      },
    },

    showBundles: {
      body: {
        ...longhand('border', {
          bottomLeftRadius: 0,
          bottomRightRadius: 0,
        }),
      },

      legal: {
        // offset 'content' container on mobile
        marginTop: r.negate(gu2px(3, spacing)),
        color: palette.primaryContrast,
      },
    },

    showBundlesSm: {
      legal: {
        marginTop: 0,
      },
    },

  }, [
    { isMinSm },
    { isMinMd },
    { isPromo },
    { availability },
    { showBundles },
    { showBundlesSm: r.and(isMinSm, showBundles) },
  ]);
};
